import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import {
  notificationsApi,
  type NotificationListParams,
  type NotificationPreference,
  type NotificationStatus,
} from "@/services/notifications";

function tenantKey(value: string | null): string {
  return value ?? "*";
}

export function useAdminNotifications(params: NotificationListParams) {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: ["notifications", "admin", tenantKey(currentTenantId), params],
    queryFn: () => notificationsApi.listAdmin(params),
    refetchInterval: 30_000,
  });
}

export function useTenantNotifications(
  tenantId: string | undefined,
  params: NotificationListParams,
) {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: [
      "notifications",
      "tenant",
      tenantKey(currentTenantId),
      tenantId,
      params,
    ],
    queryFn: () => notificationsApi.listForTenant(tenantId!, params),
    enabled: Boolean(tenantId),
    refetchInterval: 30_000,
  });
}

export function useNotificationInbox(
  params: NotificationListParams & {
    type?: string;
    priority?: string;
    groupBy?: string;
  },
) {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: ["notifications", "inbox", tenantKey(currentTenantId), params],
    queryFn: () => notificationsApi.inbox(params),
    refetchInterval: 15_000,
  });
}

export function useUnreadNotificationCount() {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: ["notifications", "unread-count", tenantKey(currentTenantId)],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 30_000,
  });
}

export function useNotificationDetail(
  id: string | undefined,
  tenantId?: string,
) {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: [
      "notification",
      tenantKey(currentTenantId),
      tenantId ?? "admin",
      id,
    ],
    queryFn: () =>
      tenantId
        ? notificationsApi.getForTenant(tenantId, id!)
        : notificationsApi.getAdmin(id!),
    enabled: Boolean(id),
  });
}

export function useRetryNotification(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      tenantId
        ? notificationsApi.retryForTenant(tenantId, id)
        : notificationsApi.retryAdmin(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notification"] });
    },
  });
}

export function useUpdateNotificationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      actionTaken,
    }: {
      id: string;
      status: NotificationStatus;
      actionTaken?: string;
    }) =>
      notificationsApi.updateStatus(id, {
        status,
        actionTaken,
        channel: "INAPP",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useBulkNotificationAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      action,
    }: {
      ids: string[];
      action: "MARK_READ" | "ARCHIVE" | "DISMISS";
    }) => notificationsApi.bulkAction(ids, action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useSendNotificationTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.sendTestAdmin,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ["notification-preferences"],
    queryFn: notificationsApi.preferences.list,
  });
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Array<
        Pick<
          NotificationPreference,
          "notificationType" | "channels" | "priorityThreshold"
        >
      >,
    ) => notificationsApi.preferences.update(body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["notification-preferences"] }),
  });
}

export function useNotificationDigestPreference() {
  return useQuery({
    queryKey: ["notification-digest-preference"],
    queryFn: notificationsApi.digest.get,
  });
}

export function useUpdateNotificationDigestPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: notificationsApi.digest.update,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["notification-digest-preference"] }),
  });
}

export function useAdminEmailTemplates() {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: ["admin-email-templates", tenantKey(currentTenantId)],
    queryFn: () => notificationsApi.catalog.list(),
  });
}

export function useAdminEmailTemplate(category: string) {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: ["admin-email-template", tenantKey(currentTenantId), category],
    queryFn: () => notificationsApi.catalog.get(category),
    enabled: Boolean(category),
  });
}

export function useAdminEmailTemplateVariables(category: string) {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: [
      "admin-email-template-variables",
      tenantKey(currentTenantId),
      category,
    ],
    queryFn: () => notificationsApi.catalog.variables(category),
    enabled: Boolean(category),
  });
}

export function useAllAdminEmailTemplateVariables() {
  const { currentTenantId } = useAuth();
  return useQuery({
    queryKey: [
      "admin-email-template-variables",
      tenantKey(currentTenantId),
      "*",
    ],
    queryFn: () => notificationsApi.catalog.allVariables(),
  });
}

export function useUpdateAdminEmailTemplate() {
  const qc = useQueryClient();
  const { currentTenantId } = useAuth();
  return useMutation({
    mutationFn: ({
      category,
      body,
    }: {
      category: string;
      body: Parameters<typeof notificationsApi.catalog.update>[1];
    }) => notificationsApi.catalog.update(category, body),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["admin-email-templates"] });
      qc.invalidateQueries({
        queryKey: [
          "admin-email-template",
          tenantKey(currentTenantId),
          variables.category,
        ],
      });
    },
  });
}

export function usePreviewAdminEmailTemplate(category: string) {
  return useMutation({
    mutationFn: ({
      variables,
      tenantId,
    }: {
      variables?: Record<string, unknown>;
      tenantId?: string;
    }) => notificationsApi.catalog.preview(category, variables, tenantId),
  });
}

export function useTestAdminEmailTemplate(category: string) {
  return useMutation({
    mutationFn: (body: Parameters<typeof notificationsApi.catalog.test>[1]) =>
      notificationsApi.catalog.test(category, body),
  });
}
